<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { UiButton, UiIcon, UiText, UiAside, UiDialog, UiSelect } from "@shopexperts/shared";
import type { ExpertLevel } from "@shopexperts/legacy-api";

import ExpertLevelCard from "@/components/admin/cards/ExpertLevelCard.vue";
import ExpertLevelForm from "@/components/admin/ExpertLevelForm.vue";

import {
  useAdminExpertLevels,
  useCreateAdminExpertLevel,
  useUpdateAdminExpertLevel,
  useDeleteAdminExpertLevel,
} from "@/app/composables/expert-levels";
import {
  defaultExpertLevelFormValues,
  expertLevelToFormValues,
  formValuesToCreatePayload,
  formValuesToUpdatePayload,
  type ExpertLevelFormValues,
} from "@/app/composables/expert-levels/expertLevelForm";
import { buildDeleteLevelCopy } from "@/app/composables/expert-levels/deleteLevelCopy";

type StatusFilter = "all" | "active" | "inactive";

const search = ref("");
const statusFilter = ref<StatusFilter>("all");

const isActiveFilter = computed<boolean | undefined>(() => {
  if (statusFilter.value === "active") return true;
  if (statusFilter.value === "inactive") return false;
  return undefined;
});

const {
  levels,
  isLoading,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useAdminExpertLevels({
  isActive: isActiveFilter,
});

const { mutateAsync: createLevel, isPending: isCreating } = useCreateAdminExpertLevel();
const { mutateAsync: updateLevel, isPending: isUpdating } = useUpdateAdminExpertLevel();
const { mutateAsync: deleteLevel, isPending: isDeleting } = useDeleteAdminExpertLevel();

const asideOpen = ref(false);
const editingLevel = ref<ExpertLevel | null>(null);
const formModel = ref<ExpertLevelFormValues>(defaultExpertLevelFormValues());
const formEl = useTemplateRef("formEl");

const deleteOpen = ref(false);
const levelPendingDelete = ref<ExpertLevel | null>(null);

const isSubmitting = computed(() => isCreating.value || isUpdating.value);

const statusOptions: readonly { label: string; value: StatusFilter }[] = [
  { label: "All levels", value: "all" },
  { label: "Active only", value: "active" },
  { label: "Inactive only", value: "inactive" },
];

const sortedLevels = computed(() => {
  const list = [...levels.value];
  list.sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.id - b.id;
  });
  return list;
});

const filteredLevels = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return sortedLevels.value;
  return sortedLevels.value.filter((level) => {
    return (
      level.public_title.toLowerCase().includes(query) ||
      (level.public_description ?? "").toLowerCase().includes(query)
    );
  });
});

const deleteCopy = computed(() => buildDeleteLevelCopy(levelPendingDelete.value));

const suggestedSortOrder = computed(() => {
  if (levels.value.length === 0) return 0;
  const max = levels.value.reduce((acc, level) => Math.max(acc, level.sort_order), -1);
  return max + 1;
});

function openCreate() {
  editingLevel.value = null;
  formModel.value = {
    ...defaultExpertLevelFormValues(),
    sort_order: suggestedSortOrder.value,
  };
  formEl.value?.resetForm();
  asideOpen.value = true;
}

function openEdit(level: ExpertLevel) {
  editingLevel.value = level;
  formModel.value = expertLevelToFormValues(level);
  asideOpen.value = true;
}

function openDelete(level: ExpertLevel) {
  levelPendingDelete.value = level;
  deleteOpen.value = true;
}

function closeAside() {
  asideOpen.value = false;
  editingLevel.value = null;
  formEl.value?.resetForm();
}

async function onFormSubmit(values: ExpertLevelFormValues) {
  try {
    if (editingLevel.value) {
      await updateLevel({
        expertLevelId: editingLevel.value.id,
        body: formValuesToUpdatePayload(values),
      });
    } else {
      await createLevel(formValuesToCreatePayload(values));
    }
    closeAside();
  } catch (err) {
    console.error("[ExpertLevels] Failed to save level:", err);
  }
}

async function confirmDelete() {
  const target = levelPendingDelete.value;
  if (!target) return;
  try {
    await deleteLevel(target.id);
    deleteOpen.value = false;
    levelPendingDelete.value = null;
  } catch (err) {
    console.error("[ExpertLevels] Failed to delete level:", err);
  }
}

function cancelDelete() {
  deleteOpen.value = false;
  levelPendingDelete.value = null;
}

function loadMore() {
  if (hasNextPage.value && !isFetchingNextPage.value) {
    fetchNextPage();
  }
}
</script>

<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
    <!-- Header -->
    <div class="flex flex-col gap-4 mb-6">
      <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div class="flex flex-col gap-1">
          <h1 class="text-3xl font-normal">Expert Levels</h1>
          <UiText
            as="p"
            variant="p"
            class="text-se-gray-600 max-w-2xl"
            text="Define the levels experts progress through. Each level sets a commission rate and an auto-enroll gross-sales threshold."
          />
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <div class="w-[180px]">
            <UiSelect
              v-model="statusFilter"
              :options="(statusOptions as unknown as Array<{ label: string; value: string }>)"
              placeholder="All levels"
              size="md"
            />
          </div>

          <div class="relative w-[260px]">
            <div class="absolute left-3 top-1/2 -translate-y-1/2">
              <UiIcon name="search" class="w-5 h-5 text-se-gray-500" />
            </div>
            <input
              v-model="search"
              type="text"
              placeholder="Search by title or description..."
              class="w-full h-9 pl-10 pr-3 py-1 border border-se-gray-300 rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-se-primary"
            />
          </div>

          <UiButton variant="primary" label="Add Expert Level" @click="openCreate">
            <template #before_label>
              <UiIcon name="plus-square" class="w-5 h-5 text-se-primary" />
            </template>
          </UiButton>
        </div>
      </div>

      <!-- Ordering hint -->
      <div
        class="flex items-center gap-2 px-4 py-2 rounded-md bg-se-gray-100 border border-se-gray-200 self-start"
      >
        <UiIcon name="info-circle" class="w-4 h-4 text-se-gray-700" />
        <UiText
          as="p"
          variant="p-small"
          class="text-se-gray-700"
          text="Levels are ordered by their sort order — lower numbers appear first. Edit a level to change its position."
        />
      </div>
    </div>

    <!-- List -->
    <div class="space-y-3">
      <div v-if="isLoading" class="py-12 text-center">
        <UiText as="p" variant="p" class="text-se-gray-600" text="Loading expert levels..." />
      </div>

      <div v-else-if="filteredLevels.length === 0" class="py-16 text-center">
        <div class="flex flex-col items-center gap-3 max-w-md mx-auto">
          <div class="w-12 h-12 rounded-full bg-se-gray-100 flex items-center justify-center">
            <UiIcon name="shopexperts-badge" class="w-6 h-6 text-se-gray-500" />
          </div>
          <UiText
            as="h6"
            variant="h6"
            class="font-semibold"
            :text="search ? 'No levels match your search' : 'No expert levels yet'"
          />
          <UiText
            as="p"
            variant="p-small"
            class="text-se-gray-600"
            :text="search
              ? 'Try a different search term or clear the filter.'
              : 'Create your first level to start auto-enrolling experts by their gross sales.'"
          />
          <UiButton v-if="!search" variant="primary" label="Add Expert Level" @click="openCreate">
            <template #before_label>
              <UiIcon name="plus-square" class="w-5 h-5" />
            </template>
          </UiButton>
        </div>
      </div>

      <template v-else>
        <ExpertLevelCard
          v-for="level in filteredLevels"
          :key="level.id"
          :level="level"
          @edit="openEdit(level)"
          @delete="openDelete(level)"
        />

        <div v-if="hasNextPage" class="flex justify-center pt-4">
          <UiButton
            variant="outline"
            :label="isFetchingNextPage ? 'Loading...' : 'Load More'"
            :disabled="isFetchingNextPage"
            @click="loadMore"
          />
        </div>
      </template>
    </div>

    <!-- Create / edit aside -->
    <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
      <div class="px-4 py-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <div class="flex items-start justify-between gap-4">
          <div class="flex flex-col gap-1">
            <UiText
              as="h3"
              variant="h3"
              class="font-semibold"
              :text="editingLevel ? 'Edit Expert Level' : 'Create Expert Level'"
            />
            <UiText
              as="p"
              variant="p-small"
              class="text-se-gray-600"
              :text="editingLevel
                ? 'Update the level\'s title, commission, threshold or visibility.'
                : 'Define a new tier experts can be auto-enrolled into.'"
            />
          </div>
          <UiButton variant="ghost" shape="square" :disabled="isSubmitting" @click="closeAside">
            <template #before_label>
              <UiIcon name="close" class="w-6 h-6" />
            </template>
          </UiButton>
        </div>

        <ExpertLevelForm
          ref="formEl"
          v-model="formModel"
          :is-editing="editingLevel !== null"
          :is-submitting="isSubmitting"
          @submit="onFormSubmit"
          @cancel="closeAside"
        />
      </div>
    </UiAside>

    <!-- Delete confirmation -->
    <UiDialog
      v-model="deleteOpen"
      v-model:loading="isDeleting"
      :title="deleteCopy.title"
      :text="deleteCopy.text"
      :primary-action-label="deleteCopy.primaryActionLabel"
      :secondary-action-label="deleteCopy.secondaryActionLabel"
      @primary-action="confirmDelete"
      @secondary-action="cancelDelete"
    />
  </main>
</template>
